import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

interface ServerToClientEvents {
  'activity:new': (activity: unknown) => void;
  'leaderboard:updated': (data: unknown) => void;
  'submission:new': (submission: unknown) => void;
  'effect:changed': (data: unknown) => void;
  error: (error: { message: string }) => void;
}

interface ClientToServerEvents {
  'facilitator:join': (
    data: { eventId: string },
    callback: (response: { success: boolean; error?: string }) => void,
  ) => void;
  'facilitator:leave': (
    data: { eventId: string },
    callback: (response: { success: boolean; error?: string }) => void,
  ) => void;
}

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface UseDisplaySocketOptions {
  /**
   * Event-scoped display token (from `?token=` on the display URL). When
   * provided it is used for the Socket.IO handshake instead of a Clerk
   * session token, so the display works on unauthenticated screens.
   */
  staticToken?: string;
}

/**
 * Hook for display page to connect to Socket.IO and receive real-time updates
 * Listens for activity feed and leaderboard events
 */
export function useDisplaySocket(eventId: string | undefined, options?: UseDisplaySocketOptions) {
  const staticToken = options?.staticToken;
  // Always call Clerk hooks (rules of hooks); staticToken wins when present.
  const { getToken, isLoaded } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<TypedSocket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // With a static display token there's no Clerk session to wait for.
    if (!eventId || (!staticToken && !isLoaded)) return;

    let socket: TypedSocket | null = null;

    const connectSocket = async () => {
      try {
        // Prefer the event-scoped display token; else the Clerk session token.
        const token = staticToken ?? (await getToken());
        if (!token) {
          console.warn('[DisplaySocket] No auth token available');
          if (mountedRef.current) setStatus('error');
          return;
        }

        // Determine socket URL
        const socketUrl = import.meta.env.VITE_API_URL || window.location.origin;
        console.log('[DisplaySocket] Connecting to:', socketUrl);

        // Create socket connection
        socket = io(socketUrl, {
          path: '/api/socket',
          transports: ['websocket', 'polling'],
          auth: { token },
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 5,
          timeout: 10000,
        }) as TypedSocket;

        socketRef.current = socket;

        socket.on('connect', () => {
          console.log('[DisplaySocket] Connected, socket id:', socket?.id);
          if (mountedRef.current) setStatus('connected');

          // Join facilitators room for this event
          socket?.emit('facilitator:join', { eventId }, (response) => {
            if (response.success) {
              console.log('[DisplaySocket] Joined event room:', eventId);
            } else {
              console.error('[DisplaySocket] Failed to join room:', response.error);
            }
          });
        });

        socket.on('disconnect', (reason) => {
          console.log('[DisplaySocket] Disconnected:', reason);
          if (mountedRef.current) setStatus('disconnected');
        });

        socket.on('connect_error', (error) => {
          console.error('[DisplaySocket] Connection error:', error.message);
          if (mountedRef.current) setStatus('error');
        });

        socket.on('error', (error) => {
          console.error('[DisplaySocket] Error:', error.message);
        });

        // Real-time event handlers.
        socket.on('activity:new', () => {
          console.log('[DisplaySocket] New activity received');
          queryClient.invalidateQueries({ queryKey: ['EventActivity', eventId] });
        });

        socket.on('leaderboard:updated', () => {
          console.log('[DisplaySocket] Leaderboard updated');
          queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
          queryClient.invalidateQueries({ queryKey: ['EventLeaderboard', eventId] });
        });

        socket.on('submission:new', () => {
          console.log('[DisplaySocket] New submission received');
          queryClient.invalidateQueries({ queryKey: ['EventSubmissions', eventId] });
          queryClient.invalidateQueries({ queryKey: ['EventSubmissionsSummary', eventId] });
        });

        // A facilitator effect was applied, retimed, or dismissed — refresh
        // the Live and team-page effect lists.
        socket.on('effect:changed', () => {
          console.log('[DisplaySocket] Effect changed');
          queryClient.invalidateQueries({ queryKey: ['EventActiveEffects', eventId] });
          queryClient.invalidateQueries({ queryKey: ['TeamEffects', eventId] });
        });
      } catch (error) {
        console.error('[DisplaySocket] Setup error:', error);
        if (mountedRef.current) setStatus('error');
      }
    };

    // Set a timeout - if not connected after 5 seconds, show error state
    const timeoutId = setTimeout(() => {
      if (mountedRef.current) {
        setStatus((current) => (current === 'connecting' ? 'error' : current));
      }
    }, 5000);

    connectSocket();

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (socket) {
        socket.emit('facilitator:leave', { eventId }, () => {});
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [eventId, isLoaded, getToken, queryClient, staticToken]);

  return {
    isConnected: status === 'connected',
    status,
  };
}
